import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase, authHelpers, db } from '../lib/supabase';
import {
  mockCohorts,
  mockUsers,
  defaultModuleTemplate,
  externalResources as defaultExternalResources
} from '../data/lmsData';

const LMSContext = createContext(null);

// Flag to use mock data (set to false when Supabase is configured)
const USE_MOCK_DATA = false;

// =========================================================================
// HELPER: Clear all navigation state from localStorage
// =========================================================================
const clearNavigationState = () => {
  try {
    localStorage.removeItem('lms_currentView');
    localStorage.removeItem('lms_selectedCohortId');
    localStorage.removeItem('lms_selectedWeek');
    localStorage.removeItem('currentCohortId');
  } catch (e) {
    // ignore localStorage errors
  }
};

// Helper: Calculate actual date string from cohort start date, week number, and day name
const calcUnlockDate = (startDateStr, weekNumber, dayOfWeek) => {
  if (!startDateStr || !dayOfWeek) return null;
  const dayMap = { 'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3, 'Thursday': 4, 'Friday': 5, 'Saturday': 6 };
  const targetDayIndex = dayMap[dayOfWeek];
  if (targetDayIndex === undefined) return null;

  const [startYear, startMonth, startDay] = startDateStr.split('-').map(Number);
  const cohortStart = new Date(startYear, startMonth - 1, startDay);
  const cohortStartDayIndex = cohortStart.getDay();

  const daysToTargetDay = (targetDayIndex - cohortStartDayIndex + 7) % 7;
  const weekOffset = ((weekNumber || 1) - 1) * 7;

  const eventDate = new Date(cohortStart);
  eventDate.setDate(eventDate.getDate() + weekOffset + daysToTargetDay);

  const year = eventDate.getFullYear();
  const month = String(eventDate.getMonth() + 1).padStart(2, '0');
  const day = String(eventDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const LMSProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [cohorts, setCohorts] = useState([]);
  const [students, setStudents] = useState([]);
  const [modules, setModules] = useState([]);

  // Keep announcements for UI compatibility (no table in schema)
  const [announcements, setAnnouncements] = useState([]);

  // Events will be merged from calendar_events + cohort_custom_events
  const [events, setEvents] = useState([]);

  // External resources - now loaded from database
  const [externalResources, setExternalResources] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [dataInitialized, setDataInitialized] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  // =========================================================================
  // NAVIGATION STATE - Persisted to localStorage
  // =========================================================================
  const [currentViewState, setCurrentViewState] = useState(() => {
    try {
      return localStorage.getItem('lms_currentView') || 'dashboard';
    } catch (e) {
      return 'dashboard';
    }
  });

  const [selectedCohortIdState, setSelectedCohortIdState] = useState(() => {
    try {
      return localStorage.getItem('lms_selectedCohortId') || null;
    } catch (e) {
      return null;
    }
  });

  const [selectedWeekState, setSelectedWeekState] = useState(() => {
    try {
      const saved = localStorage.getItem('lms_selectedWeek');
      return saved ? parseInt(saved, 10) : null;
    } catch (e) {
      return null;
    }
  });

  // =========================================================================
  // NAVIGATION SETTERS - Persist to localStorage on change
  // =========================================================================
  const setCurrentView = useCallback((view) => {
    setCurrentViewState(view);
    try {
      localStorage.setItem('lms_currentView', view);
    } catch (e) {
      // ignore localStorage errors
    }
  }, []);

  const setSelectedCohortId = useCallback((cohortId) => {
    setSelectedCohortIdState(cohortId);
    try {
      if (cohortId) {
        localStorage.setItem('lms_selectedCohortId', cohortId);
      } else {
        localStorage.removeItem('lms_selectedCohortId');
      }
    } catch (e) {
      // ignore localStorage errors
    }
  }, []);

  const setSelectedWeek = useCallback((week) => {
    setSelectedWeekState(week);
    try {
      if (week !== null && week !== undefined) {
        localStorage.setItem('lms_selectedWeek', String(week));
      } else {
        localStorage.removeItem('lms_selectedWeek');
      }
    } catch (e) {
      // ignore localStorage errors
    }
  }, []);

  // Expose state values with cleaner names
  const currentView = currentViewState;
  const selectedCohortId = selectedCohortIdState;
  const selectedWeek = selectedWeekState;

  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  const [studentProgress, setStudentProgress] = useState({});
  const [studentSubmissions, setStudentSubmissions] = useState([]);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [defaultZoomUrl, setDefaultZoomUrl] = useState('https://zoom.us/j/1234567890');
  
  // Cohort-specific item overrides (for drag-and-drop within a cohort)
  const [cohortItemOverrides, setCohortItemOverrides] = useState({});

  // Cohort-specific module visibility overrides (per-cohort lock/unlock)
  const [cohortVisibility, setCohortVisibility] = useState({});

  // Persist dark mode preference
  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  // =========================================================================
  // ADDED: toggleDarkMode function (Sidebar expects this, not setIsDarkMode)
  // =========================================================================
  const toggleDarkMode = useCallback(() => {
    setIsDarkMode(prev => !prev);
  }, []);

  // Track the current cohort selection
  const [currentCohortId, setCurrentCohortId] = useState(null);

  // Helper function to set the current cohort
  const setCurrentCohort = useCallback((cohortId) => {
    setCurrentCohortId(cohortId);
    setSelectedCohortId(cohortId); // Keep both in sync (now persists to localStorage)
    try {
      localStorage.setItem('currentCohortId', cohortId);
    } catch (e) {
      // ignore
    }
  }, [setSelectedCohortId]);

  // Derived helpers
  const activeCohortId = useMemo(() => {
    return selectedCohortId || currentCohortId || cohorts[0]?.id || null;
  }, [selectedCohortId, currentCohortId, cohorts]);

  const currentCohort = useMemo(() => {
    return cohorts.find(c => c.id === activeCohortId) || cohorts[0] || null;
  }, [cohorts, activeCohortId]);

  const currentCohortStudents = useMemo(() => {
    if (!activeCohortId) return [];
    return students.filter(s => s.cohortId === activeCohortId);
  }, [students, activeCohortId]);

  const currentCohortModules = useMemo(() => {
    return modules;
  }, [modules]);

  const currentCohortAnnouncements = useMemo(() => {
    if (!activeCohortId) return [];
    return announcements.filter(a => a.cohortId === activeCohortId);
  }, [announcements, activeCohortId]);

  const currentCohortEvents = useMemo(() => {
    if (!activeCohortId) return [];
    return events.filter(e => e.cohortId === activeCohortId);
  }, [events, activeCohortId]);

  // =========================================================================
  // ADDED: getCohortById function (CohortView, CalendarView, Sidebar need this)
  // =========================================================================
  const getCohortById = useCallback((cohortId) => {
    if (!cohortId) return null;
    return cohorts.find(c => c.id === cohortId) || null;
  }, [cohorts]);

  // =========================================================================
  // ADDED: getModulesForCohort (returns all modules - for admin)
  // =========================================================================
  const getModulesForCohort = useCallback((cohortId) => {
    // Get overrides for this cohort
    const overrides = cohortItemOverrides[cohortId] || {};
    const visOverrides = cohortVisibility[cohortId] || {};

    // Modules are global templates; return all with normalized structure
    // Apply cohort-specific overrides to items AND visibility
    return modules.map(m => {
      const visOverride = visOverrides[m.id];
      // If a cohort-specific visibility override exists, use it; otherwise fall back to master
      const isVisible = visOverride
        ? (visOverride.is_visible ?? false)
        : (m.isVisible ?? m.is_visible ?? false);
      const visibleDate = visOverride
        ? (visOverride.visible_date || null)
        : (m.visibleDate || m.visible_date || null);

      return {
        ...m,
        weekNumber: m.weekNumber || m.week_number,
        isVisible,
        visibleDate,
        items: (m.items || m.module_items || []).map(item => {
          const override = overrides[item.id];
          if (override) {
            return {
              ...item,
              dueDay: override.due_day ?? override.dueDay ?? item.dueDay ?? item.due_day,
              itemOrder: override.item_order ?? override.itemOrder ?? item.itemOrder ?? item.item_order,
              dueDate: override.due_date ?? override.dueDate ?? item.dueDate ?? null
            };
          }
          return item;
        })
      };
    });
  }, [modules, cohortItemOverrides, cohortVisibility]);

  // =========================================================================
  // ADDED: getVisibleModulesForCohort (returns only visible modules - for students)
  // =========================================================================
  const getVisibleModulesForCohort = useCallback((cohortId) => {
    const cohort = getCohortById(cohortId);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDateStr = cohort ? (cohort.startDate || cohort.start_date) : null;

    return getModulesForCohort(cohortId).filter(m => {
      // Already explicitly visible
      if (m.isVisible) return true;

      // Check if visibleAfter schedule has been reached
      // visibleAfter format: "week:day" e.g. "3:Thursday"
      if (m.visibleAfter && startDateStr) {
        const [weekStr, day] = m.visibleAfter.split(':');
        const weekNum = parseInt(weekStr, 10);
        if (weekNum && day) {
          const unlockDateStr = calcUnlockDate(startDateStr, weekNum, day);
          if (unlockDateStr) {
            const [y, mo, d] = unlockDateStr.split('-').map(Number);
            const unlockDate = new Date(y, mo - 1, d);
            return today >= unlockDate;
          }
        }
      }

      return false;
    });
  }, [getModulesForCohort, getCohortById]);

  // =========================================================================
  // ADDED: getUsersForCohort (returns students in a cohort)
  // =========================================================================
  const getUsersForCohort = useCallback((cohortId) => {
    if (!cohortId) return [];
    return students.filter(s => s.cohortId === cohortId);
  }, [students]);

  // =========================================================================
  // ADDED: getEventsForCohort (returns events for a cohort)
  // =========================================================================
  const getEventsForCohort = useCallback((cohortId) => {
    if (!cohortId) return [];
    const cohort = getCohortById(cohortId);
    if (!cohort) return [];

    // Get cohort start date for calculating event dates
    // Parse date string as local time to avoid timezone issues
    const startDateStr = cohort.startDate || cohort.start_date;
    if (!startDateStr) return [];
    
    const [startYear, startMonth, startDay] = startDateStr.split('-').map(Number);
    const cohortStart = new Date(startYear, startMonth - 1, startDay);
    
    // Helper to calculate actual date from week number and day
    // This calculates which specific date a "Monday of Week 2" falls on for this cohort
    const calculateEventDate = (weekNumber, dayOfWeek) => {
      const dayMap = { 'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3, 'Thursday': 4, 'Friday': 5, 'Saturday': 6 };
      const targetDayIndex = dayMap[dayOfWeek] ?? 0;
      const cohortStartDayIndex = cohortStart.getDay();
      
      // Calculate days from cohort start to reach Week 1 of the target day
      // Week 1 starts on the cohort start date
      // If cohort starts Saturday (6) and target is Monday (1):
      //   daysToTargetDay = (1 - 6 + 7) % 7 = 2 days forward to Monday
      // If cohort starts Saturday (6) and target is Friday (5):
      //   daysToTargetDay = (5 - 6 + 7) % 7 = 6 days forward to Friday
      // If cohort starts Saturday (6) and target is Saturday (6):
      //   daysToTargetDay = (6 - 6 + 7) % 7 = 0 days (same day)
      let daysToTargetDay = (targetDayIndex - cohortStartDayIndex + 7) % 7;
      
      // Add weeks offset (week 1 = 0 weeks, week 2 = 1 week, etc.)
      const weekOffset = ((weekNumber || 1) - 1) * 7;
      
      const eventDate = new Date(cohortStart);
      eventDate.setDate(eventDate.getDate() + weekOffset + daysToTargetDay);
      
      // Format as YYYY-MM-DD in local time
      const year = eventDate.getFullYear();
      const month = String(eventDate.getMonth() + 1).padStart(2, '0');
      const day = String(eventDate.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Map calendar_events (master template) to actual dates based on cohort start
    const mappedTemplateEvents = events
      .filter(e => e.source === 'template')
      .map(e => ({
        ...e,
        id: `template-${e.id}-${cohortId}`,
        date: calculateEventDate(e.week_number, e.day_of_week),
        cohortId,
        isFromMasterTemplate: true,
        source: 'template'
      }));

    // Get cohort-specific custom events
    const customEvents = events
      .filter(e => e.source === 'cohort_custom' && e.cohortId === cohortId)
      .map(e => ({
        ...e,
        date: e.event_date || e.date,
        isFromMasterTemplate: false,
        source: 'cohort_custom'
      }));

    // Get cohort-specific overrides
    const overrides = cohortItemOverrides[cohortId] || {};

    // Generate events from module items (Master Modules auto-population)
    // Check for cohort-specific overrides for the due day
    const moduleItemEvents = modules
      .filter(m => m.isVisible || m.is_visible) // Only visible modules
      .flatMap(module => {
        const weekNum = module.weekNumber || module.week_number;
        const items = module.items || [];
        
        return items
          .filter(item => {
            // Check if there's an override for this item
            const override = overrides[item.id];
            const effectiveDueDay = override?.due_day || item.dueDay || item.due_day;
            return effectiveDueDay; // Only items with a day set (master or override)
          })
          .map(item => {
            // Use override due day if exists, otherwise use master
            const override = overrides[item.id];
            const dueDay = override?.due_day || item.dueDay || item.due_day;
            const itemType = item.type;
            
            // Map item types to event types
            const typeMap = {
              'assignment': 'assignment',
              'quiz': 'quiz',
              'exam': 'exam',
              'video': 'lecture',
              'live-lecture': 'lecture',
              'start-here': 'other',
              'about-course': 'other',
              'workbook': 'assignment',
              'resource': 'other'
            };
            
            return {
              id: `module-item-${item.id}-${cohortId}`,
              title: item.title,
              type: typeMap[itemType] || 'other',
              date: calculateEventDate(weekNum, dueDay),
              weekNumber: weekNum,
              dayOfWeek: dueDay,
              cohortId,
              source: 'module_item',
              isFromModule: true,
              moduleItemId: item.id,
              itemType: itemType,
              points: item.points,
              description: item.description,
              startTime: item.startTime || item.start_time || null,
              endTime: item.endTime || item.end_time || null,
              videoUrl: item.videoUrl || item.video_url || null,
              hasOverride: !!override // Flag if this has a cohort-specific override
            };
          });
      });

    // Filter out events that fall outside the cohort date range.
    // This prevents phantom dates (e.g., Week 1 Tuesday/Thursday showing
    // before the cohort start when the cohort starts on a Saturday).
    // ONLY filter template and module_item events (calculated dates).
    // Custom events are intentionally set by the professor, so leave them alone.
    const endDateStr = cohort.endDate || cohort.end_date;
    const allEvents = [...mappedTemplateEvents, ...customEvents, ...moduleItemEvents];
    
    const boundedEvents = allEvents.filter(e => {
      // Custom events have manually chosen dates - never filter them out
      if (e.source === 'cohort_custom') return true;
      if (!e.date) return true;
      if (startDateStr && e.date < startDateStr) return false; // Before cohort start
      if (endDateStr && e.date > endDateStr) return false;     // After cohort end
      return true;
    });

    return boundedEvents.sort((a, b) => {
      // Sort by date first (soonest first)
      const dateCompare = (a.date || '').localeCompare(b.date || '');
      if (dateCompare !== 0) return dateCompare;
      // Then by start time within the same day
      const timeA = a.startTime || a.start_time || '';
      const timeB = b.startTime || b.start_time || '';
      return timeA.localeCompare(timeB);
    });
  }, [events, modules, getCohortById, cohortItemOverrides]);

  // =========================================================================
  // ADDED: toggleWeekVisibility (toggle module visibility for a cohort)
  // =========================================================================
  const toggleWeekVisibility = useCallback(async (cohortId, weekNumber) => {
    // Find the module
    const module = modules.find(m => (m.weekNumber || m.week_number) === weekNumber);
    if (!module || !cohortId) return;

    // Check cohort-specific visibility first, then fall back to master
    const visOverrides = cohortVisibility[cohortId] || {};
    const visOverride = visOverrides[module.id];
    const currentVisibility = visOverride
      ? (visOverride.is_visible ?? false)
      : (module.isVisible ?? module.is_visible ?? false);
    const newVisibility = !currentVisibility;

    if (USE_MOCK_DATA) {
      setCohortVisibility(prev => ({
        ...prev,
        [cohortId]: {
          ...(prev[cohortId] || {}),
          [module.id]: { is_visible: newVisibility, visible_date: null }
        }
      }));
      return;
    }

    try {
      // Write to cohort_module_visibility (per-cohort, not master)
      const { error } = await db.updateCohortVisibility(cohortId, module.id, {
        is_visible: newVisibility,
        visible_date: null
      });

      if (error) throw error;

      // Update local cohortVisibility state (not modules)
      setCohortVisibility(prev => ({
        ...prev,
        [cohortId]: {
          ...(prev[cohortId] || {}),
          [module.id]: {
            ...(prev[cohortId]?.[module.id] || {}),
            is_visible: newVisibility,
            visible_date: null
          }
        }
      }));
    } catch (err) {
      console.error('Error toggling visibility:', err);
    }
  }, [modules, cohortVisibility]);

  // =========================================================================
  // ADDED: scheduleWeekVisibility (schedule when a module becomes visible)
  // =========================================================================
  const scheduleWeekVisibility = useCallback(async (cohortId, weekNumber, date) => {
    const module = modules.find(m => (m.weekNumber || m.week_number) === weekNumber);
    if (!module || !cohortId) return;

    if (USE_MOCK_DATA) {
      setCohortVisibility(prev => ({
        ...prev,
        [cohortId]: {
          ...(prev[cohortId] || {}),
          [module.id]: { is_visible: false, visible_date: date }
        }
      }));
      return;
    }

    try {
      const { error } = await db.updateCohortVisibility(cohortId, module.id, {
        visible_date: date,
        is_visible: false // Will become visible on the scheduled date
      });
      if (error) throw error;

      // Update local cohortVisibility state (not master modules)
      setCohortVisibility(prev => ({
        ...prev,
        [cohortId]: {
          ...(prev[cohortId] || {}),
          [module.id]: {
            ...(prev[cohortId]?.[module.id] || {}),
            is_visible: false,
            visible_date: date
          }
        }
      }));
    } catch (err) {
      console.error('Error scheduling visibility:', err);
    }
  }, [modules]);

  // =========================================================================
  // ADDED: addMasterCalendarEvent (add event to master template - all cohorts)
  // Also creates a corresponding module item for bidirectional sync
  // =========================================================================
  const addMasterCalendarEvent = useCallback(async (eventData) => {
    try {
      // 1. Create the calendar event
      const newEvent = {
        id: `master-${Date.now()}`,
        ...eventData,
        week_number: eventData.weekNumber,
        day_of_week: eventData.dayOfWeek,
        source: 'template'
      };
      
      if (!USE_MOCK_DATA) {
        // Save to calendar_events table
        const { data, error } = await supabase
          .from('calendar_events')
          .insert({
            type: eventData.type || 'other',
            title: eventData.title,
            week_number: eventData.weekNumber,
            day_of_week: eventData.dayOfWeek,
            start_time: eventData.startTime || null,
            end_time: eventData.endTime || null,
            points: eventData.points || null,
            is_from_module: eventData.alsoAddToModule || false
          })
          .select()
          .single();
        
        if (error) throw error;
        newEvent.id = data.id;
      }
      
      setEvents(prev => [...prev, newEvent]);

      // 2. If alsoAddToModule is true, create a corresponding module item
      if (eventData.alsoAddToModule) {
        const weekNumber = eventData.weekNumber;
        const module = modules.find(m => (m.weekNumber || m.week_number) === weekNumber);
        
        if (module) {
          // Map calendar event type to module item type
          const typeMap = {
            'lecture': 'live-lecture',
            'assignment': 'assignment',
            'quiz': 'quiz',
            'exam': 'exam',
            'other': 'resource'
          };
          
          const moduleItemPayload = {
            module_id: module.id,
            type: typeMap[eventData.type] || 'resource',
            title: eventData.title,
            description: eventData.description || null,
            due_day: eventData.dayOfWeek,
            points: eventData.points || 0,
            item_order: (module.items?.length || 0) + 1
          };
          
          if (USE_MOCK_DATA) {
            const newItem = normalizeModuleItem({
              id: `item-${Date.now()}`,
              ...moduleItemPayload
            });
            setModules(prev => prev.map(m => 
              m.id === module.id 
                ? { ...m, items: [...(m.items || []), newItem] }
                : m
            ));
          } else {
            const { data: itemData, error: itemError } = await db.createModuleItem(moduleItemPayload);
            if (!itemError && itemData) {
              const normalized = normalizeModuleItem(itemData);
              setModules(prev => prev.map(m => 
                m.id === module.id 
                  ? { ...m, items: [...(m.items || []), normalized] }
                  : m
              ));
            }
          }
        }
      }

      return { event: newEvent, error: null };
    } catch (error) {
      console.error('Error adding master calendar event:', error);
      return { event: null, error: error.message || String(error) };
    }
  }, [modules]);

  // =========================================================================
  // ADDED: addCohortCustomEvent (add event to specific cohort)
  // =========================================================================
  const addCohortCustomEvent = useCallback(async (cohortId, eventData) => {
    try {
      if (USE_MOCK_DATA) {
        const newEvent = {
          id: `custom-${Date.now()}`,
          ...eventData,
          cohortId,
          source: 'cohort_custom'
        };
        setEvents(prev => [...prev, newEvent]);
        return { event: newEvent, error: null };
      }

      const payload = {
        cohort_id: cohortId,
        type: eventData.type || 'other',
        title: eventData.title,
        event_date: eventData.date,
        start_time: eventData.startTime || null,
        end_time: eventData.endTime || null,
        description: eventData.description || null
      };

      const { data, error } = await db.createCohortEvent(payload);
      if (error) throw error;

      const normalized = { 
        ...data, 
        source: 'cohort_custom', 
        cohortId: data.cohort_id,
        date: data.event_date
      };
      setEvents(prev => [...prev, normalized]);
      return { event: normalized, error: null };
    } catch (error) {
      console.error('Error adding cohort event:', error);
      return { event: null, error: error.message || String(error) };
    }
  }, []);

  // =========================================================================
  // ADDED: deleteCalendarEvent (delete a cohort-specific event)
  // =========================================================================
  const deleteCalendarEvent = useCallback(async (eventId) => {
    try {
      if (USE_MOCK_DATA) {
        setEvents(prev => prev.filter(e => e.id !== eventId));
        return { error: null };
      }

      const { error } = await db.deleteCohortEvent(eventId);
      if (error) throw error;

      setEvents(prev => prev.filter(e => e.id !== eventId));
      return { error: null };
    } catch (error) {
      console.error('Error deleting event:', error);
      return { error: error.message || String(error) };
    }
  }, []);

  // =========================================================================
  // ADDED: deleteMasterCalendarEvent (delete from master template)
  // =========================================================================
  const deleteMasterCalendarEvent = useCallback(async (eventId) => {
    // For now, just remove from local state
    // In production, this would delete from calendar_events table
    setEvents(prev => prev.filter(e => e.id !== eventId));
    return { error: null };
  }, []);

  // =========================================================================
  // EXTERNAL RESOURCES FUNCTIONS (Database-backed)
  // =========================================================================
  const addExternalResource = useCallback(async (resourceData) => {
    try {
      if (USE_MOCK_DATA) {
        const newResource = {
          id: `resource-${Date.now()}`,
          ...resourceData
        };
        setExternalResources(prev => [...prev, newResource]);
        return { resource: newResource, error: null };
      }

      const payload = {
        title: resourceData.title,
        url: resourceData.url,
        icon: resourceData.icon || '🔗',
        description: resourceData.description || null,
        display_order: externalResources.length + 1
      };

      const { data, error } = await db.createExternalResource(payload);
      if (error) throw error;

      setExternalResources(prev => [...prev, data]);
      return { resource: data, error: null };
    } catch (error) {
      console.error('Error adding external resource:', error);
      return { resource: null, error: error.message || String(error) };
    }
  }, [externalResources.length]);

  const deleteExternalResource = useCallback(async (resourceId) => {
    try {
      if (USE_MOCK_DATA) {
        setExternalResources(prev => prev.filter(r => r.id !== resourceId));
        return { error: null };
      }

      const { error } = await db.deleteExternalResource(resourceId);
      if (error) throw error;

      setExternalResources(prev => prev.filter(r => r.id !== resourceId));
      return { error: null };
    } catch (error) {
      console.error('Error deleting external resource:', error);
      return { error: error.message || String(error) };
    }
  }, []);

  const updateExternalResource = useCallback(async (resourceId, resourceData) => {
    try {
      if (USE_MOCK_DATA) {
        setExternalResources(prev => prev.map(r => 
          r.id === resourceId ? { ...r, ...resourceData } : r
        ));
        return { resource: { id: resourceId, ...resourceData }, error: null };
      }

      const { data, error } = await db.updateExternalResource(resourceId, resourceData);
      if (error) throw error;

      setExternalResources(prev => prev.map(r => 
        r.id === resourceId ? data : r
      ));
      return { resource: data, error: null };
    } catch (error) {
      console.error('Error updating external resource:', error);
      return { resource: null, error: error.message || String(error) };
    }
  }, []);

  // =========================================================================
  // NORMALIZERS
  // =========================================================================
  const computeCohortStatus = (startDateStr, endDateStr) => {
    if (!startDateStr || !endDateStr) return 'upcoming';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [sY, sM, sD] = startDateStr.split('-').map(Number);
    const [eY, eM, eD] = endDateStr.split('-').map(Number);
    const start = new Date(sY, sM - 1, sD);
    const end = new Date(eY, eM - 1, eD, 23, 59, 59, 999);
    if (today > end) return 'completed';
    if (today >= start) return 'active';
    return 'upcoming';
  };

  const normalizeCohort = (raw) => {
    const startDate = raw.start_date || raw.startDate;
    const endDate = raw.end_date || raw.endDate;
    return {
      id: raw.id,
      name: raw.name,
      startDate,
      endDate,
      status: computeCohortStatus(startDate, endDate),
      maxCapacity: raw.max_capacity ?? raw.maxCapacity ?? 30,
      enrolledCount: raw.enrolledCount ?? 0,
      lectureSchedule: raw.lecture_schedule || raw.lectureSchedule || {
        days: ['Monday', 'Wednesday', 'Friday'],
        startTime: '11:30',
        endTime: '15:00',
        timezone: 'EST'
      },
      syllabusPdfs: raw.syllabusPdfs || []
    };
  };

  const normalizeModule = (raw) => ({
    id: raw.id,
    weekNumber: raw.week_number ?? raw.weekNumber,
    title: raw.title,
    isVisible: raw.is_visible ?? raw.isVisible ?? false,
    visibleDate: raw.visible_date || raw.visibleDate || null,
    visibleAfter: raw.visible_after ?? raw.visibleAfter ?? null,
    items: (raw.module_items || raw.items || []).map(normalizeModuleItem)
  });

  const normalizeModuleItem = (raw) => ({
    id: raw.id,
    moduleId: raw.module_id || raw.moduleId,
    type: raw.type,
    title: raw.title,
    content: raw.content,
    videoUrl: raw.video_url || raw.videoUrl,
    videoType: raw.video_type || raw.videoType || 'youtube',
    duration: raw.duration,
    description: raw.description,
    dueDay: raw.due_day || raw.dueDay,
    points: raw.points || 0,
    questionCount: raw.question_count || raw.questionCount || 0,
    timeLimit: raw.time_limit || raw.timeLimit || 0,
    itemOrder: raw.item_order ?? raw.itemOrder ?? 1,
    startTime: raw.start_time || raw.startTime || null,
    endTime: raw.end_time || raw.endTime || null,
    pdfUrl: raw.pdf_url || raw.pdfUrl || null,
    pdfName: raw.pdf_name || raw.pdfName || null,
    attachments: raw.attachments || null,
    countsForEligibility: raw.counts_for_eligibility || raw.countsForEligibility || false
  });

  const normalizeStudent = (raw) => ({
    id: raw.id,
    email: raw.email,
    name: raw.name,
    role: raw.role || 'student',
    status: raw.status || 'active',
    cohortId: raw.cohort_id || raw.cohortId,
    createdAt: raw.created_at || raw.createdAt,
    password_set: raw.password_set !== undefined ? raw.password_set : true,
    // Profile fields
    avatar_url: raw.avatar_url,
    tiktok_handle: raw.tiktok_handle,
    instagram_handle: raw.instagram_handle,
    facebook_handle: raw.facebook_handle,
    nclex_test_date: raw.nclex_test_date || null,
    // Thinkific NCLEX course link (only populated for admins via the
    // Thinkific Enrollment view; null/undefined for everyone else).
    thinkific_user_id: raw.thinkific_user_id ?? null,
    thinkific_enrollment_id: raw.thinkific_enrollment_id ?? null,
    thinkific_status: raw.thinkific_status ?? null,
    thinkific_synced_at: raw.thinkific_synced_at ?? null,
    // Wave 1 progress stats - cached from Thinkific enrollment data
    thinkific_percentage_completed: raw.thinkific_percentage_completed ?? null,
    thinkific_started_at: raw.thinkific_started_at ?? null,
    thinkific_completed_at: raw.thinkific_completed_at ?? null,
    thinkific_activated_at: raw.thinkific_activated_at ?? null,
    thinkific_expiry_date: raw.thinkific_expiry_date ?? null,
    thinkific_completed: raw.thinkific_completed ?? null,
    // Wave 2: real-time activity from Thinkific webhooks
    thinkific_last_signin_at: raw.thinkific_last_signin_at ?? null,
    // Wave 3: paid vs free distinction
    thinkific_paid: raw.thinkific_paid ?? false,
    thinkific_first_paid_at: raw.thinkific_first_paid_at ?? null
  });

  // =========================================================================
  // AUTH & DATA LOADING
  // =========================================================================
  // -------------------------------------------------------------------------
  // FIX: White screen on refresh + admin role demotion
  //
  // WHAT WAS BROKEN:
  //   The old code called db.getProfile() inside Supabase's onAuthStateChange
  //   callback. Supabase blocks its own database calls during this callback,
  //   creating a deadlock on every page refresh. The profile fetch would hang
  //   until a timeout fired, then the fallback hardcoded the role to 'student'.
  //   This caused two bugs:
  //     1. Admin users got demoted to student view on refresh.
  //     2. The app sometimes got stuck on a white loading screen forever
  //        when multiple auth events (SIGNED_IN, INITIAL_SESSION) raced
  //        against each other and the complex flag system dropped the fetch.
  //
  // WHY THE OLD APPROACH DOESN'T WORK:
  //   Supabase's onAuthStateChange is a synchronous listener. Any async
  //   Supabase DB call (like .from('profiles').select()) made inside it
  //   gets queued behind the listener's own lock. The call never resolves
  //   until the callback finishes, but the callback is awaiting the call.
  //   Deadlock. Every time.
  //
  // HOW THIS FIX WORKS:
  //   Separated auth detection from profile loading into two useEffects:
  //     Step 1 (auth): getSession() on mount + onAuthStateChange listener.
  //       ONLY sets user state. Zero database calls.
  //     Step 2 (profile): Separate useEffect watches user?.id. Fetches
  //       the profile OUTSIDE the callback where DB calls work normally.
  //       Uses a cancellation ref to prevent stale fetches.
  //   This is the same pattern used in CardChase1, Demo, and other
  //   working apps (see AUTH_PATTERN.md).
  // -------------------------------------------------------------------------

  const [authChecked, setAuthChecked] = useState(false);
  const profileFetchRef = useRef(null);

  // STEP 1: Auth — get initial session, then listen. NO database calls.
  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;

        if (session?.user) {
          setUser(session.user);
          setIsAuthenticated(true);
        } else {
          setUser(null);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Auth init error:', err);
        if (isMounted) setIsLoading(false);
      }

      if (isMounted) setAuthChecked(true);
    };

    initializeAuth();

    const { data: { subscription } } = authHelpers.onAuthStateChange((event, session) => {
      if (!isMounted) return;

      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true);
        setUser(session.user);
        setIsAuthenticated(true);
        setIsLoading(false);
        return;
      }

      if (event === 'SIGNED_OUT' || !session) {
        setUser(null);
        setIsAuthenticated(false);
        setCurrentUser(null);
        setDataInitialized(false);
        setIsLoading(false);
        clearNavigationState();
        return;
      }

      if (session?.user) {
        setUser(session.user);
        setIsAuthenticated(true);
      }
    });

    return () => {
      isMounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  // STEP 2: Profile loader — runs OUTSIDE auth callback, watches user?.id
  useEffect(() => {
    if (!authChecked) return;

    if (!user?.id) {
      setCurrentUser(null);
      setIsLoading(false);
      return;
    }

    if (profileFetchRef.current) {
      profileFetchRef.current.cancelled = true;
    }

    const fetchProfile = async () => {
      const fetchState = { cancelled: false };
      profileFetchRef.current = fetchState;

      try {
        const { data: profile, error } = await db.getProfile(user.id);

        if (fetchState.cancelled) return;

        if (profile && !error) {
          console.log('Profile loaded — role:', profile.role);
          setCurrentUser(normalizeStudent(profile));

          if (profile.role === 'student' && profile.cohort_id) {
            setSelectedCohortId(profile.cohort_id);
            setCurrentCohortId(profile.cohort_id);
          }
        } else {
          console.warn('No profile found, using session data');
          setCurrentUser({
            id: user.id,
            email: user.email,
            name: user.user_metadata?.name || user.email?.split('@')[0] || 'User',
            role: user.user_metadata?.role || 'student',
            status: 'active',
            password_set: true
          });
        }
      } catch (err) {
        console.error('Profile fetch error:', err);
        if (!fetchState.cancelled) {
          setCurrentUser({
            id: user.id,
            email: user.email,
            name: user.email?.split('@')[0] || 'User',
            role: 'student',
            status: 'active',
            password_set: true
          });
        }
      } finally {
        if (!fetchState.cancelled) setIsLoading(false);
      }
    };

    fetchProfile();

    return () => {
      if (profileFetchRef.current) {
        profileFetchRef.current.cancelled = true;
      }
    };
  }, [user?.id, authChecked, setSelectedCohortId]);

  // Load data when authenticated
  useEffect(() => {
    if (!isAuthenticated || dataInitialized) return;

    const loadData = async () => {
      try {
        if (USE_MOCK_DATA) {
          setCohorts(mockCohorts.map(normalizeCohort));
          setStudents(mockUsers.map(normalizeStudent));
          setModules(defaultModuleTemplate.map(normalizeModule));
          setExternalResources(defaultExternalResources);
          setDataInitialized(true);
          return;
        }

        // Load from Supabase
        const [cohortsRes, profilesRes, modulesRes, calendarRes] = await Promise.all([
          db.getCohorts(),
          db.getAllProfiles(),
          db.getModules(),
          db.getCalendarEvents()
        ]);

        if (cohortsRes.data) {
          setCohorts(cohortsRes.data.map(normalizeCohort));
        }
        if (profilesRes.data) {
          setStudents(profilesRes.data.map(normalizeStudent));
        }
        if (modulesRes.data) {
          setModules(modulesRes.data.map(normalizeModule));
        }
        if (calendarRes.data) {
          setEvents(calendarRes.data.map(e => ({ ...e, source: 'template' })));
        }

        // Load default zoom URL setting
        try {
          const { data: zoomSetting } = await db.getSetting('default_zoom_url');
          if (zoomSetting?.value) {
            setDefaultZoomUrl(zoomSetting.value);
          }
        } catch (err) {
          console.log('Using default zoom URL');
        }

        setDataInitialized(true);
      } catch (error) {
        console.error('Error loading data:', error);
        // Still set dataInitialized to true to prevent infinite loading
        setDataInitialized(true);
      }
    };

    loadData();
  }, [isAuthenticated, dataInitialized]);

  // Load cohort-specific events when activeCohortId changes
  useEffect(() => {
    if (!activeCohortId || USE_MOCK_DATA) return;

    const loadCohortEvents = async () => {
      try {
        const { data: cohortEvents } = await db.getCohortCustomEvents(activeCohortId);
        if (cohortEvents) {
          setEvents(prev => {
            // Remove old cohort_custom events and add new ones
            const filtered = prev.filter(e => e.source !== 'cohort_custom');
            return [
              ...filtered,
              ...cohortEvents.map(e => ({ ...e, source: 'cohort_custom', cohortId: e.cohort_id }))
            ];
          });
        }
      } catch (error) {
        console.error('Error loading cohort events:', error);
      }
    };

    loadCohortEvents();
  }, [activeCohortId]);

  // Load external resources when authenticated
  useEffect(() => {
    if (!isAuthenticated || USE_MOCK_DATA) return;

    const loadExternalResources = async () => {
      try {
        const { data, error } = await db.getExternalResources();
        if (error) throw error;
        if (data) {
          setExternalResources(data);
        }
      } catch (error) {
        console.error('Error loading external resources:', error);
        // Fall back to defaults if database fails
        setExternalResources(defaultExternalResources);
      }
    };

    loadExternalResources();
  }, [isAuthenticated]);

  // Separate effect to load student progress when currentUser is available
  useEffect(() => {
    if (!currentUser?.id || currentUser?.role !== 'student' || USE_MOCK_DATA) return;

    const loadStudentProgress = async () => {
      try {
        const { data: progress } = await db.getUserProgress(currentUser.id);
        if (progress) {
          const progressMap = {};
          progress.forEach(p => {
            progressMap[p.module_item_id] = p;
          });
          setStudentProgress(progressMap);
        }
      } catch (error) {
        console.error('Error loading student progress:', error);
      }
    };

    const loadStudentSubmissions = async () => {
      try {
        const { data: submissions } = await db.getStudentSubmissions(currentUser.id);
        if (submissions) {
          setStudentSubmissions(submissions);
        }
      } catch (error) {
        console.error('Error loading student submissions:', error);
      }
    };

    const loadUnreadCount = async () => {
      try {
        const { count } = await db.getUnreadCount(currentUser.id);
        setUnreadMessageCount(count || 0);
      } catch (error) {
        console.error('Error loading unread count:', error);
      }
    };

    loadStudentProgress();
    loadStudentSubmissions();
    loadUnreadCount();
    
    // Poll for unread messages every 30 seconds
    const unreadInterval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(unreadInterval);
  }, [currentUser?.id, currentUser?.role]);

  // =========================================================================
  // AUTH FUNCTIONS
  // =========================================================================
  const login = async (email, password) => {
    try {
      const { data, error } = await authHelpers.signIn(email, password);
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  const signup = async (name, email, password, cohortId) => {
    try {
      const { data, error } = await authHelpers.signUp(email, password, {
        name,
        cohort_id: cohortId,
        role: 'student'
      });
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      console.log('Logout called');
      const { error } = await authHelpers.signOut();
      console.log('SignOut result:', error);
      
      if (error) {
        console.error('SignOut error:', error);
        return { success: false, error: error.message };
      }
      
      // Manually clear state in case SIGNED_OUT event doesn't fire
      setUser(null);
      setIsAuthenticated(false);
      setCurrentUser(null);
      setDataInitialized(false);
      clearNavigationState();
      
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    }
  };

  const getUpcomingCohorts = useCallback(() => {
    return cohorts.filter(c => c.status === 'upcoming' || c.status === 'active');
  }, [cohorts]);

  // =========================================================================
  // SYLLABUS PDF FUNCTIONS
  // =========================================================================
  const addSyllabusPdf = useCallback(async (cohortId, pdfData) => {
    try {
      if (USE_MOCK_DATA) {
        const newPdf = { id: Date.now().toString(), ...pdfData };
        setCohorts(prev => prev.map(c => 
          c.id === cohortId 
            ? { ...c, syllabusPdfs: [...(c.syllabusPdfs || []), newPdf] }
            : c
        ));
        return { pdf: newPdf, error: null };
      }

      const { data, error } = await db.uploadSyllabusPdf(cohortId, pdfData);
      if (error) throw error;

      return { pdf: data, error: null };
    } catch (error) {
      console.error('Error adding syllabus PDF:', error);
      return { pdf: null, error: error.message || String(error) };
    }
  }, []);

  const deleteSyllabusPdf = useCallback(async (cohortId, pdfId) => {
    try {
      if (USE_MOCK_DATA) {
        setCohorts(prev => prev.map(c => 
          c.id === cohortId 
            ? { ...c, syllabusPdfs: (c.syllabusPdfs || []).filter(p => p.id !== pdfId) }
            : c
        ));
        return { error: null };
      }

      const { error } = await db.deleteSyllabusPdf(pdfId);
      if (error) throw error;

      return { error: null };
    } catch (error) {
      console.error('Error deleting syllabus PDF:', error);
      return { error: error.message || String(error) };
    }
  }, []);

  // ===========================================================================
  // COHORT CRUD
  // ===========================================================================
  const addCohort = async (cohortData) => {
    try {
      if (USE_MOCK_DATA) {
        const newCohort = normalizeCohort({
          id: Date.now().toString(),
          ...cohortData,
          modules: [],
          announcements: [],
          events: []
        });
        setCohorts([...cohorts, newCohort]);
        return { cohort: newCohort, error: null };
      }

      const { data, error } = await db.createCohort(cohortData);
      if (error) throw error;

      const normalized = normalizeCohort(data);
      setCohorts(prev => [...prev, normalized]);
      return { cohort: normalized, error: null };
    } catch (error) {
      console.error('Error adding cohort:', error);
      return { cohort: null, error: error.message || String(error) };
    }
  };

  const updateCohort = async (cohortId, cohortData) => {
    try {
      if (USE_MOCK_DATA) {
        const updatedCohorts = cohorts.map(cohort =>
          cohort.id === cohortId ? normalizeCohort({ ...cohort, ...cohortData }) : cohort
        );
        setCohorts(updatedCohorts);
        return { error: null };
      }

      const { data, error } = await db.updateCohort(cohortId, cohortData);
      if (error) throw error;

      setCohorts(prev => prev.map(c => (c.id === cohortId ? normalizeCohort(data) : c)));
      return { error: null };
    } catch (error) {
      console.error('Error updating cohort:', error);
      return { error: error.message || String(error) };
    }
  };

  const deleteCohort = async (cohortId) => {
    try {
      if (USE_MOCK_DATA) {
        const filteredCohorts = cohorts.filter(cohort => cohort.id !== cohortId);
        setCohorts(filteredCohorts);
        return { error: null };
      }

      const { error } = await supabase.from('cohorts').delete().eq('id', cohortId);
      if (error) throw error;

      const filteredCohorts = cohorts.filter(cohort => cohort.id !== cohortId);
      setCohorts(filteredCohorts);

      if (currentCohortId === cohortId && filteredCohorts.length > 0) {
        setCurrentCohort(filteredCohorts[0].id);
      } else if (filteredCohorts.length === 0) {
        setCurrentCohortId(null);
        setSelectedCohortId(null);
        clearNavigationState();
      }

      return { error: null };
    } catch (error) {
      console.error('Error deleting cohort:', error);
      return { error: error.message || String(error) };
    }
  };

  const duplicateCohort = async (sourceCohortId, newCohortData) => {
    try {
      // 1. Get the source cohort
      const sourceCohort = cohorts.find(c => c.id === sourceCohortId);
      if (!sourceCohort) {
        return { cohort: null, error: 'Source cohort not found' };
      }

      // 2. Create the new cohort
      const cohortPayload = {
        name: newCohortData.name,
        start_date: newCohortData.startDate,
        end_date: newCohortData.endDate,
        status: 'upcoming',
        max_capacity: sourceCohort.maxCapacity || sourceCohort.max_capacity || 30,
        lecture_schedule: sourceCohort.lectureSchedule || sourceCohort.lecture_schedule || {
          days: ['Monday', 'Wednesday', 'Friday'],
          startTime: '11:30',
          endTime: '15:00',
          timezone: 'EST'
        }
      };

      if (USE_MOCK_DATA) {
        const newCohort = normalizeCohort({
          id: Date.now().toString(),
          ...cohortPayload
        });
        setCohorts(prev => [...prev, newCohort]);
        return { cohort: newCohort, error: null };
      }

      // Create cohort in database
      const { data: newCohort, error: cohortError } = await db.createCohort(cohortPayload);
      if (cohortError) throw cohortError;

      // 3. Calculate date offset for copying events
      const sourceStart = new Date(sourceCohort.startDate || sourceCohort.start_date);
      const newStart = new Date(newCohortData.startDate);
      const dayOffset = Math.floor((newStart - sourceStart) / (1000 * 60 * 60 * 24));

      // 4. Copy cohort-specific custom events
      const { data: sourceEvents } = await supabase
        .from('cohort_custom_events')
        .select('*')
        .eq('cohort_id', sourceCohortId);

      if (sourceEvents && sourceEvents.length > 0) {
        const newEvents = sourceEvents.map(event => {
          // Adjust event date by the offset
          const originalDate = new Date(event.event_date);
          const newDate = new Date(originalDate);
          newDate.setDate(newDate.getDate() + dayOffset);
          
          return {
            cohort_id: newCohort.id,
            type: event.type,
            title: event.title,
            event_date: newDate.toISOString().split('T')[0],
            start_time: event.start_time,
            end_time: event.end_time,
            description: event.description
          };
        });

        await supabase.from('cohort_custom_events').insert(newEvents);
      }

      // 5. Copy cohort-specific syllabus PDFs (reference only, not re-uploading files)
      const { data: sourcePdfs } = await supabase
        .from('syllabus_pdfs')
        .select('*')
        .eq('cohort_id', sourceCohortId);

      if (sourcePdfs && sourcePdfs.length > 0) {
        const newPdfs = sourcePdfs.map(pdf => ({
          cohort_id: newCohort.id,
          title: pdf.title,
          filename: pdf.filename,
          file_url: pdf.file_url,
          file_size: pdf.file_size
        }));

        await supabase.from('syllabus_pdfs').insert(newPdfs);
      }

      // 6. Copy cohort module visibility settings
      const { data: sourceVisibility } = await supabase
        .from('cohort_module_visibility')
        .select('*')
        .eq('cohort_id', sourceCohortId);

      if (sourceVisibility && sourceVisibility.length > 0) {
        const newVisibility = sourceVisibility.map(v => ({
          cohort_id: newCohort.id,
          module_id: v.module_id,
          is_visible: v.is_visible,
          visible_date: v.visible_date
        }));

        await supabase.from('cohort_module_visibility').insert(newVisibility);
      }

      // 7. Update local state
      const normalized = normalizeCohort(newCohort);
      setCohorts(prev => [...prev, normalized]);

      return { cohort: normalized, error: null };
    } catch (error) {
      console.error('Error duplicating cohort:', error);
      return { cohort: null, error: error.message || String(error) };
    }
  };

  // ===========================================================================
  // STUDENT CRUD
  // ===========================================================================

  // Pull the latest profiles from the database into the local students state.
  // Used after Thinkific grant/revoke actions so the UI reflects updated
  // thinkific_status / thinkific_enrollment_id without a page refresh.
  const refreshStudents = async () => {
    if (USE_MOCK_DATA) return { error: null };
    const { data, error } = await db.getAllProfiles();
    if (error) return { error: error.message || String(error) };
    if (data) setStudents(data.map(normalizeStudent));
    return { error: null };
  };

  const addStudent = async (studentData) => {
    try {
      if (USE_MOCK_DATA) {
        const newStudent = normalizeStudent({
          id: Date.now().toString(),
          ...studentData,
          createdAt: new Date().toISOString()
        });
        setStudents(prev => [...prev, newStudent]);
        return { student: newStudent, error: null };
      }

      // Use the appropriate admin method based on creation method
      const method = studentData.method || 'invite'; // default to invite
      const authMethod = method === 'invite' 
        ? authHelpers.adminInviteUser 
        : authHelpers.adminCreateUser;

      const { data, error } = await authMethod({
        email: studentData.email,
        name: studentData.name,
        role: studentData.role || 'student',
        cohortId: studentData.cohortId || null,
      });

      if (error) {
        return { student: null, error: error.message };
      }

      // The DB trigger auto-creates the profile. Refresh students list.
      // Small delay to allow the trigger to complete.
      await new Promise(resolve => setTimeout(resolve, 1000));

      const { data: profiles } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (profiles) {
        setStudents(profiles.map(normalizeStudent));
      }

      return { 
        student: data?.user, 
        error: null, 
        message: data?.message,
        tempPassword: data?.user?.tempPassword || null,
      };
    } catch (error) {
      console.error('Error adding student:', error);
      return { student: null, error: error.message || String(error) };
    }
  };

  const updateStudent = async (studentId, studentData) => {
    try {
      if (USE_MOCK_DATA) {
        setStudents(prev => prev.map(s =>
          s.id === studentId ? normalizeStudent({ ...s, ...studentData }) : s
        ));
        return { error: null };
      }

      const updates = {
        name: studentData.name,
        email: studentData.email,
        role: studentData.role,
        status: studentData.status,
        cohort_id: studentData.cohortId
      };
      if (studentData.nclex_test_date !== undefined) {
        updates.nclex_test_date = studentData.nclex_test_date || null;
      }

      // Social media handles: editable by admins and clerical staff.
      // Empty strings are stored as NULL so cleared fields don't leave
      // stale values behind.
      const canEditSocial = currentUser?.role === 'admin' || currentUser?.role === 'clerical';
      if (canEditSocial) {
        if (studentData.tiktok_handle !== undefined) {
          updates.tiktok_handle = studentData.tiktok_handle?.trim() || null;
        }
        if (studentData.instagram_handle !== undefined) {
          updates.instagram_handle = studentData.instagram_handle?.trim() || null;
        }
        if (studentData.facebook_handle !== undefined) {
          updates.facebook_handle = studentData.facebook_handle?.trim() || null;
        }
      }

      const { data, error } = await db.updateProfile(studentId, updates);
      if (error) throw error;

      setStudents(prev => prev.map(s => 
        s.id === studentId ? normalizeStudent(data) : s
      ));
      return { error: null };
    } catch (error) {
      console.error('Error updating student:', error);
      return { error: error.message || String(error) };
    }
  };

  const deleteStudent = async (studentId) => {
    try {
      if (USE_MOCK_DATA) {
        setStudents(prev => prev.filter(s => s.id !== studentId));
        return { error: null };
      }

      // Use the admin API to fully delete the user (auth + profile)
      const { error } = await authHelpers.adminDeleteUser(studentId);
      if (error) throw new Error(error.message);

      setStudents(prev => prev.filter(s => s.id !== studentId));
      return { error: null };
    } catch (error) {
      console.error('Error deleting student:', error);
      return { error: error.message || String(error) };
    }
  };

  // ===========================================================================
  // STUDENT SELF-SERVICE PROFILE UPDATE
  // ===========================================================================
  const updateOwnProfile = async (updates) => {
    try {
      if (!currentUser) return { error: 'Not logged in' };

      // Only allow students to update safe fields
      const safeUpdates = {};
      if (updates.name !== undefined) safeUpdates.name = updates.name;
      if (updates.nclex_test_date !== undefined) safeUpdates.nclex_test_date = updates.nclex_test_date || null;
      if (updates.tiktok_handle !== undefined) safeUpdates.tiktok_handle = updates.tiktok_handle;
      if (updates.instagram_handle !== undefined) safeUpdates.instagram_handle = updates.instagram_handle;
      if (updates.facebook_handle !== undefined) safeUpdates.facebook_handle = updates.facebook_handle;
      if (updates.avatar_url !== undefined) safeUpdates.avatar_url = updates.avatar_url;

      if (Object.keys(safeUpdates).length === 0) return { error: null };

      if (USE_MOCK_DATA) {
        setCurrentUser(prev => ({ ...prev, ...safeUpdates }));
        return { error: null };
      }

      const { data, error } = await db.updateProfile(currentUser.id, safeUpdates);
      if (error) throw error;

      setCurrentUser(prev => ({ ...prev, ...safeUpdates }));
      // Also update in students list if present
      setStudents(prev => prev.map(s =>
        s.id === currentUser.id ? normalizeStudent({ ...s, ...safeUpdates }) : s
      ));
      return { error: null };
    } catch (error) {
      console.error('Error updating own profile:', error);
      return { error: error.message || String(error) };
    }
  };

  // ===========================================================================
  // MODULE ITEM FUNCTIONS
  // ===========================================================================

  // Helper: Calculate actual date from cohort start, week number, and day name
  const calculateDateForCohort = (cohort, weekNumber, dayOfWeek) => {
    const startDateStr = cohort.startDate || cohort.start_date;
    return calcUnlockDate(startDateStr, weekNumber, dayOfWeek);
  };

  // Helper: Create attendance sessions for a live-lecture item across all cohorts
  const syncLiveLectureAttendance = async (moduleItem, weekNumber) => {
    if (moduleItem.type !== 'live-lecture') return;
    
    const dueDay = moduleItem.dueDay || moduleItem.due_day;
    if (!dueDay) return;

    try {
      for (const cohort of cohorts) {
        const cohortId = cohort.id;
        const sessionDate = calculateDateForCohort(cohort, weekNumber, dueDay);
        if (!sessionDate) continue;

        // Skip dates that fall outside the cohort's date range
        // This prevents phantom attendance sessions (e.g., Feb 4/6 appearing
        // when the cohort starts Feb 8 but lectures are Tue/Thu)
        const cohortStartStr = cohort.startDate || cohort.start_date;
        const cohortEndStr = cohort.endDate || cohort.end_date;
        if (cohortStartStr && sessionDate < cohortStartStr) continue;
        if (cohortEndStr && sessionDate > cohortEndStr) continue;

        // Check if an attendance session already exists for this item + cohort + date
        const { data: existing } = await supabase
          .from('attendance_sessions')
          .select('id')
          .eq('cohort_id', cohortId)
          .eq('linked_module_item_id', moduleItem.id)
          .maybeSingle();

        if (existing) {
          // Update the existing session's date/time if changed
          await supabase
            .from('attendance_sessions')
            .update({
              title: moduleItem.title,
              session_date: sessionDate,
              start_time: moduleItem.startTime || moduleItem.start_time || null,
              end_time: moduleItem.endTime || moduleItem.end_time || null,
            })
            .eq('id', existing.id);
        } else {
          // Create a new attendance session
          await supabase
            .from('attendance_sessions')
            .insert({
              cohort_id: cohortId,
              title: moduleItem.title,
              session_type: 'lecture',
              session_date: sessionDate,
              start_time: moduleItem.startTime || moduleItem.start_time || null,
              end_time: moduleItem.endTime || moduleItem.end_time || null,
              notes: `Auto-created from Week ${weekNumber} - ${dueDay}`,
              linked_module_item_id: moduleItem.id,
              created_by: currentUser?.id
            });
        }
      }
    } catch (err) {
      console.error('Error syncing live lecture attendance:', err);
    }
  };

  const addModuleItem = async (moduleId, itemData) => {
    try {
      const module = modules.find(m => m.id === moduleId);
      if (!module) {
        return { item: null, error: 'Module not found' };
      }

      if (USE_MOCK_DATA) {
        const newItem = normalizeModuleItem({
          id: Date.now().toString(),
          module_id: moduleId,
          ...itemData,
          item_order: (module.items?.length || 0) + 1
        });
        
        setModules(prev => prev.map(m => 
          m.id === moduleId 
            ? { ...m, items: [...(m.items || []), newItem] }
            : m
        ));
        return { item: newItem, error: null };
      }

      const payload = {
        module_id: moduleId,
        type: itemData.type,
        title: itemData.title,
        content: itemData.content || null,
        video_url: itemData.videoUrl || null,
        video_type: itemData.videoType || 'youtube',
        duration: itemData.duration || null,
        description: itemData.description || null,
        due_day: itemData.dueDay || null,
        points: itemData.points || 0,
        question_count: itemData.questionCount || 0,
        time_limit: itemData.timeLimit || 0,
        item_order: (module.items?.length || 0) + 1,
        start_time: itemData.startTime || null,
        end_time: itemData.endTime || null,
        pdf_url: itemData.pdfUrl || null,
        pdf_name: itemData.pdfName || null,
        ...(itemData.attachments ? { attachments: itemData.attachments } : {}),
        counts_for_eligibility: itemData.countsForEligibility || false
      };

      const { data, error } = await db.createModuleItem(payload);
      if (error) throw error;

      const normalized = normalizeModuleItem(data);
      setModules(prev => prev.map(m => 
        m.id === moduleId 
          ? { ...m, items: [...(m.items || []), normalized] }
          : m
      ));

      // If this is a calendar-worthy item type with a due day, 
      // it will automatically appear on the calendar via getEventsForCohort
      // No need to create a separate calendar_event since the module item 
      // is already shown on the calendar through the moduleItemEvents mapping

      // Auto-create attendance sessions for live lectures
      if (normalized.type === 'live-lecture' && normalized.dueDay) {
        const weekNumber = module.weekNumber || module.week_number;
        await syncLiveLectureAttendance(normalized, weekNumber);
      }

      return { item: normalized, error: null };
    } catch (error) {
      console.error('Error adding module item:', error);
      return { item: null, error: error.message || String(error) };
    }
  };

  const updateModuleItem = async (itemId, itemData) => {
    try {
      if (USE_MOCK_DATA) {
        setModules(prev => prev.map(m => ({
          ...m,
          items: (m.items || []).map(item => 
            item.id === itemId ? normalizeModuleItem({ ...item, ...itemData }) : item
          )
        })));
        return { error: null };
      }

      const payload = {
        title: itemData.title,
        content: itemData.content,
        video_url: itemData.videoUrl,
        video_type: itemData.videoType,
        duration: itemData.duration,
        description: itemData.description,
        due_day: itemData.dueDay,
        points: itemData.points,
        question_count: itemData.questionCount,
        time_limit: itemData.timeLimit,
        item_order: itemData.itemOrder,
        start_time: itemData.startTime,
        end_time: itemData.endTime,
        pdf_url: itemData.pdfUrl,
        pdf_name: itemData.pdfName,
        ...(itemData.attachments !== undefined ? { attachments: itemData.attachments } : {}),
        counts_for_eligibility: itemData.countsForEligibility !== undefined ? itemData.countsForEligibility : undefined
      };

      // Remove undefined values
      Object.keys(payload).forEach(key => {
        if (payload[key] === undefined) delete payload[key];
      });

      const { data, error } = await db.updateModuleItem(itemId, payload);
      if (error) throw error;

      const normalized = normalizeModuleItem(data);
      setModules(prev => prev.map(m => ({
        ...m,
        items: (m.items || []).map(item => 
          item.id === itemId ? normalized : item
        )
      })));

      // If a live-lecture's day/time was modified, update attendance sessions
      if (normalized.type === 'live-lecture' && (itemData.dueDay || itemData.startTime || itemData.endTime || itemData.title)) {
        // Find the module this item belongs to for the week number
        const parentModule = modules.find(m => (m.items || []).some(i => i.id === itemId));
        if (parentModule) {
          const weekNumber = parentModule.weekNumber || parentModule.week_number;
          await syncLiveLectureAttendance(normalized, weekNumber);
        }
      }

      return { error: null };
    } catch (error) {
      console.error('Error updating module item:', error);
      return { error: error.message || String(error) };
    }
  };

  const deleteModuleItem = async (itemId) => {
    try {
      if (USE_MOCK_DATA) {
        setModules(prev => prev.map(m => ({
          ...m,
          items: (m.items || []).filter(item => item.id !== itemId)
        })));
        return { error: null };
      }

      const { error } = await db.deleteModuleItem(itemId);
      if (error) throw error;

      setModules(prev => prev.map(m => ({
        ...m,
        items: (m.items || []).filter(item => item.id !== itemId)
      })));
      return { error: null };
    } catch (error) {
      console.error('Error deleting module item:', error);
      return { error: error.message || String(error) };
    }
  };

  // ===========================================================================
  // COHORT-SPECIFIC MODULE ITEM FUNCTIONS
  // ===========================================================================
  const addCohortModuleItem = useCallback(async (cohortId, weekNumber, itemData) => {
    try {
      const module = modules.find(m => (m.weekNumber || m.week_number) === weekNumber);
      if (!module) {
        return { item: null, error: 'Module not found for this week' };
      }

      if (USE_MOCK_DATA) {
        const newItem = {
          id: Date.now().toString(),
          ...itemData,
          cohortId,
          moduleId: module.id,
          itemOrder: 100 // Place after master items
        };
        console.log('Mock: Would add cohort-specific item:', newItem);
        return { item: newItem, error: null };
      }

      const payload = {
        cohort_id: cohortId,
        module_id: module.id,
        type: itemData.type,
        title: itemData.title,
        content: itemData.content || null,
        video_url: itemData.videoUrl || null,
        video_type: itemData.videoType || 'youtube',
        description: itemData.description || null,
        due_day: itemData.dueDay || null,
        start_time: itemData.startTime || null,
        end_time: itemData.endTime || null,
        points: itemData.points || 0,
        question_count: itemData.questionCount || 0,
        time_limit: itemData.timeLimit || 0,
        item_order: 100, // Place after master items
        counts_for_eligibility: itemData.countsForEligibility || false
      };

      const { data, error } = await supabase
        .from('cohort_module_items')
        .insert(payload)
        .select()
        .single();

      if (error) throw error;

      // Refresh data to include the new item
      // In a full implementation, you'd update local state directly
      return { item: data, error: null };
    } catch (error) {
      console.error('Error adding cohort module item:', error);
      return { item: null, error: error.message || String(error) };
    }
  }, [modules]);

  const deleteCohortModuleItem = useCallback(async (cohortId, itemId) => {
    try {
      if (USE_MOCK_DATA) {
        console.log('Mock: Would delete cohort-specific item:', itemId);
        return { error: null };
      }

      const { error } = await supabase
        .from('cohort_module_items')
        .delete()
        .eq('id', itemId)
        .eq('cohort_id', cohortId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting cohort module item:', error);
      return { error: error.message || String(error) };
    }
  }, []);

  // ===========================================================================
  // PROGRESS TRACKING FUNCTIONS (ADDED - were missing!)
  // ===========================================================================
  const markItemComplete = useCallback(async (itemId, shouldBeComplete = true) => {
    if (!currentUser?.id) return;

    try {
      if (USE_MOCK_DATA) {
        setStudentProgress(prev => ({
          ...prev,
          [itemId]: { 
            ...prev[itemId],
            completed: shouldBeComplete, 
            completed_at: shouldBeComplete ? new Date().toISOString() : null
          }
        }));
        return { error: null };
      }

      let data, error;
      if (shouldBeComplete) {
        const result = await db.markItemCompleted(currentUser.id, itemId);
        data = result.data;
        error = result.error;
      } else {
        const result = await db.markItemIncomplete(currentUser.id, itemId);
        data = result.data;
        error = result.error;
      }
      
      if (error) throw error;

      setStudentProgress(prev => ({
        ...prev,
        [itemId]: data
      }));

      return { error: null };
    } catch (error) {
      console.error('Error marking item complete:', error);
      return { error: error.message || String(error) };
    }
  }, [currentUser?.id]);

  // Track when student clicks into/views an item (separate from marking complete)
  const trackItemView = useCallback(async (itemId) => {
    if (!currentUser?.id) return;

    try {
      if (USE_MOCK_DATA) {
        setStudentProgress(prev => ({
          ...prev,
          [itemId]: { 
            ...prev[itemId],
            viewed_at: prev[itemId]?.viewed_at || new Date().toISOString()
          }
        }));
        return { error: null };
      }

      const { data, error } = await db.trackItemViewed(currentUser.id, itemId);
      
      if (error) throw error;

      setStudentProgress(prev => ({
        ...prev,
        [itemId]: data
      }));

      return { error: null };
    } catch (error) {
      console.error('Error tracking item view:', error);
      return { error: error.message || String(error) };
    }
  }, [currentUser?.id]);

  const isItemComplete = useCallback((itemId) => {
    return studentProgress[itemId]?.completed === true;
  }, [studentProgress]);

  const isItemViewed = useCallback((itemId) => {
    return studentProgress[itemId]?.viewed_at != null;
  }, [studentProgress]);

  const isModuleComplete = useCallback((weekNumber) => {
    const module = modules.find(m => (m.weekNumber || m.week_number) === weekNumber);
    if (!module || !module.items || module.items.length === 0) return false;
    
    return module.items.every(item => studentProgress[item.id]?.completed === true);
  }, [modules, studentProgress]);

  const getModuleProgress = useCallback((weekNumber) => {
    const module = modules.find(m => (m.weekNumber || m.week_number) === weekNumber);
    if (!module || !module.items || module.items.length === 0) {
      return { completed: 0, total: 0, percentage: 0 };
    }

    const total = module.items.length;
    const completed = module.items.filter(item => 
      studentProgress[item.id]?.completed === true
    ).length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

    return { completed, total, percentage };
  }, [modules, studentProgress]);

  // Get submission for a specific item
  const getSubmissionForItem = useCallback((itemId) => {
    return studentSubmissions.find(s => s.module_item_id === itemId);
  }, [studentSubmissions]);

  // Get submissions with feedback (for dashboard)
  const getSubmissionsWithFeedback = useCallback(() => {
    return studentSubmissions.filter(s => s.feedback && s.feedback.trim() !== '');
  }, [studentSubmissions]);

  // Refresh submissions (call after submitting)
  const refreshStudentSubmissions = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const { data: submissions } = await db.getStudentSubmissions(currentUser.id);
      if (submissions) {
        setStudentSubmissions(submissions);
      }
    } catch (error) {
      console.error('Error refreshing submissions:', error);
    }
  }, [currentUser?.id]);

  // Refresh unread message count
  const refreshUnreadCount = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const { count } = await db.getUnreadCount(currentUser.id);
      setUnreadMessageCount(count || 0);
    } catch (error) {
      console.error('Error refreshing unread count:', error);
    }
  }, [currentUser?.id]);

  // ===========================================================================
  // COHORT ITEM OVERRIDE FUNCTIONS (for cohort-specific drag-and-drop)
  // ===========================================================================
  
  // Load overrides for a cohort
  // Load cohort-specific module visibility from cohort_module_visibility table
  const loadCohortVisibility = useCallback(async (cohortId) => {
    if (!cohortId || USE_MOCK_DATA) return;

    try {
      const { data, error } = await db.getCohortVisibility(cohortId);
      if (error) throw error;

      // Convert array to object keyed by module_id
      const visMap = {};
      (data || []).forEach(row => {
        visMap[row.module_id] = row;
      });

      setCohortVisibility(prev => ({
        ...prev,
        [cohortId]: visMap
      }));
    } catch (error) {
      console.error('Error loading cohort visibility:', error);
    }
  }, []);

  // Load cohort visibility when activeCohortId changes
  useEffect(() => {
    if (activeCohortId && !USE_MOCK_DATA) {
      loadCohortVisibility(activeCohortId);
    }
  }, [activeCohortId, loadCohortVisibility]);

  const loadCohortItemOverrides = useCallback(async (cohortId) => {
    if (!cohortId || USE_MOCK_DATA) return;
    
    try {
      const { data, error } = await db.getCohortItemOverrides(cohortId);
      if (error) throw error;
      
      // Convert array to object keyed by module_item_id
      const overridesMap = {};
      (data || []).forEach(override => {
        overridesMap[override.module_item_id] = override;
      });
      
      setCohortItemOverrides(prev => ({
        ...prev,
        [cohortId]: overridesMap
      }));
    } catch (error) {
      console.error('Error loading cohort item overrides:', error);
    }
  }, []);

  // Update an item's day for a specific cohort
  const updateCohortItemDay = useCallback(async (cohortId, moduleItemId, newDueDay) => {
    if (!cohortId || !moduleItemId) return { error: 'Missing required parameters' };
    
    try {
      if (USE_MOCK_DATA) {
        setCohortItemOverrides(prev => ({
          ...prev,
          [cohortId]: {
            ...(prev[cohortId] || {}),
            [moduleItemId]: { due_day: newDueDay }
          }
        }));
        return { error: null };
      }

      const { data, error } = await db.upsertCohortItemOverride(cohortId, moduleItemId, {
        dueDay: newDueDay
      });
      
      if (error) throw error;
      
      // Update local state
      setCohortItemOverrides(prev => ({
        ...prev,
        [cohortId]: {
          ...(prev[cohortId] || {}),
          [moduleItemId]: data
        }
      }));

      // If this is a live-lecture, update the attendance session for this cohort only
      const moduleItem = modules.flatMap(m => m.items || []).find(i => i.id === moduleItemId);
      if (moduleItem && moduleItem.type === 'live-lecture') {
        const parentModule = modules.find(m => (m.items || []).some(i => i.id === moduleItemId));
        const cohort = cohorts.find(c => c.id === cohortId);
        if (parentModule && cohort) {
          const weekNumber = parentModule.weekNumber || parentModule.week_number;
          const sessionDate = calculateDateForCohort(cohort, weekNumber, newDueDay);
          // Only create/update attendance if the date falls within the cohort range
          const cohortStartStr = cohort.startDate || cohort.start_date;
          const cohortEndStr = cohort.endDate || cohort.end_date;
          const inRange = sessionDate && 
            (!cohortStartStr || sessionDate >= cohortStartStr) && 
            (!cohortEndStr || sessionDate <= cohortEndStr);
          if (inRange) {
            // Check for existing linked session
            const { data: existing } = await supabase
              .from('attendance_sessions')
              .select('id')
              .eq('cohort_id', cohortId)
              .eq('linked_module_item_id', moduleItemId)
              .maybeSingle();

            if (existing) {
              await supabase
                .from('attendance_sessions')
                .update({ session_date: sessionDate })
                .eq('id', existing.id);
            } else {
              await supabase
                .from('attendance_sessions')
                .insert({
                  cohort_id: cohortId,
                  title: moduleItem.title,
                  session_type: 'lecture',
                  session_date: sessionDate,
                  start_time: moduleItem.startTime || null,
                  end_time: moduleItem.endTime || null,
                  notes: `Auto-created from Week ${weekNumber} - ${newDueDay} (cohort override)`,
                  linked_module_item_id: moduleItemId,
                  created_by: currentUser?.id
                });
            }
          }
        }
      }
      
      return { error: null };
    } catch (error) {
      console.error('Error updating cohort item day:', error);
      return { error: error.message || String(error) };
    }
  }, [modules, cohorts, currentUser]);

  // Update the due date for an item in a specific cohort
  const updateCohortItemDueDate = useCallback(async (cohortId, moduleItemId, dueDate) => {
    if (!cohortId || !moduleItemId) return { error: 'Missing required parameters' };

    try {
      // Optimistic local state update
      setCohortItemOverrides(prev => ({
        ...prev,
        [cohortId]: {
          ...(prev[cohortId] || {}),
          [moduleItemId]: {
            ...(prev[cohortId]?.[moduleItemId] || {}),
            due_date: dueDate
          }
        }
      }));

      if (USE_MOCK_DATA) {
        return { error: null };
      }

      const { data, error } = await db.upsertCohortItemOverride(cohortId, moduleItemId, {
        dueDate: dueDate
      });

      if (error) throw error;

      // Reconcile with server response
      setCohortItemOverrides(prev => ({
        ...prev,
        [cohortId]: {
          ...(prev[cohortId] || {}),
          [moduleItemId]: data
        }
      }));

      return { error: null };
    } catch (error) {
      console.error('Error updating cohort item due date:', error);
      if (loadCohortItemOverrides) {
        loadCohortItemOverrides(cohortId);
      }
      return { error: error.message || String(error) };
    }
  }, [loadCohortItemOverrides]);

  // Reorder an item within a cohort (cohort-specific item_order override)
  const updateCohortItemOrder = useCallback(async (cohortId, moduleItemId, newOrder) => {
    if (!cohortId || !moduleItemId) return { error: 'Missing required parameters' };
    
    try {
      // Optimistic local state update FIRST so the UI re-renders immediately
      setCohortItemOverrides(prev => ({
        ...prev,
        [cohortId]: {
          ...(prev[cohortId] || {}),
          [moduleItemId]: {
            ...(prev[cohortId]?.[moduleItemId] || {}),
            item_order: newOrder
          }
        }
      }));

      if (USE_MOCK_DATA) {
        return { error: null };
      }

      const { data, error } = await db.upsertCohortItemOverride(cohortId, moduleItemId, {
        itemOrder: newOrder
      });
      
      if (error) throw error;
      
      // Reconcile with server response (preserves any fields the server returned)
      setCohortItemOverrides(prev => ({
        ...prev,
        [cohortId]: {
          ...(prev[cohortId] || {}),
          [moduleItemId]: data
        }
      }));

      return { error: null };
    } catch (error) {
      console.error('Error updating cohort item order:', error);
      // Revert optimistic update on failure by reloading overrides
      if (loadCohortItemOverrides) {
        loadCohortItemOverrides(cohortId);
      }
      return { error: error.message };
    }
  }, [loadCohortItemOverrides]);
  
  // Reset an item to use master schedule (delete override)
  const resetCohortItemToMaster = useCallback(async (cohortId, moduleItemId) => {
    if (!cohortId || !moduleItemId) return { error: 'Missing required parameters' };
    
    try {
      if (USE_MOCK_DATA) {
        setCohortItemOverrides(prev => {
          const newOverrides = { ...(prev[cohortId] || {}) };
          delete newOverrides[moduleItemId];
          return { ...prev, [cohortId]: newOverrides };
        });
        return { error: null };
      }

      const { error } = await db.deleteCohortItemOverride(cohortId, moduleItemId);
      if (error) throw error;
      
      // Update local state
      setCohortItemOverrides(prev => {
        const newOverrides = { ...(prev[cohortId] || {}) };
        delete newOverrides[moduleItemId];
        return { ...prev, [cohortId]: newOverrides };
      });
      
      return { error: null };
    } catch (error) {
      console.error('Error resetting cohort item:', error);
      return { error: error.message || String(error) };
    }
  }, []);

  // ===========================================================================
  // MODULE FUNCTIONS
  // ===========================================================================
  const addModule = async (moduleData) => {
    try {
      if (USE_MOCK_DATA) {
        const newModule = normalizeModule({
          id: Date.now().toString(),
          ...defaultModuleTemplate,
          ...moduleData,
          items: []
        });
        setModules([...modules, newModule]);
        return { module: newModule, error: null };
      }

      // Create module in Supabase
      const payload = {
        week_number: moduleData.weekNumber || moduleData.week_number,
        title: moduleData.title,
        is_visible: moduleData.isVisible ?? moduleData.is_visible ?? false,
        visible_date: moduleData.visibleDate || moduleData.visible_date || null
      };

      const { data, error } = await supabase
        .from('modules')
        .insert(payload)
        .select()
        .single();

      if (error) throw error;

      const normalized = normalizeModule({ ...data, module_items: [] });
      setModules(prev => [...prev, normalized]);
      return { module: normalized, error: null };
    } catch (error) {
      console.error('Error adding module:', error);
      return { module: null, error: error.message || String(error) };
    }
  };

  const updateModule = async (moduleId, moduleData) => {
    try {
      if (USE_MOCK_DATA) {
        setModules(prev => prev.map(m => 
          m.id === moduleId 
            ? {
                ...m,
                ...moduleData,
                title: moduleData.title,
                isVisible: moduleData.isVisible ?? m.isVisible,
                is_visible: moduleData.isVisible ?? m.is_visible,
                visibleAfter: moduleData.visibleAfter !== undefined ? moduleData.visibleAfter : m.visibleAfter,
                visible_after: moduleData.visibleAfter !== undefined ? moduleData.visibleAfter : m.visible_after
              }
            : m
        ));
        return { error: null };
      }

      const payload = {};
      if (moduleData.title !== undefined) payload.title = moduleData.title;
      if (moduleData.isVisible !== undefined) payload.is_visible = moduleData.isVisible;
      if (moduleData.visibleDate !== undefined) payload.visible_date = moduleData.visibleDate;
      if (moduleData.visibleAfter !== undefined) payload.visible_after = moduleData.visibleAfter;

      const { data, error } = await supabase
        .from('modules')
        .update(payload)
        .eq('id', moduleId)
        .select()
        .single();

      if (error) throw error;

      // Update local state
      setModules(prev => prev.map(m => 
        m.id === moduleId 
          ? normalizeModule({ ...m, ...data })
          : m
      ));

      return { error: null };
    } catch (error) {
      console.error('Error updating module:', error);
      return { error: error.message || String(error) };
    }
  };

  const deleteModule = async (moduleId) => {
    try {
      if (USE_MOCK_DATA) {
        setModules(prev => prev.filter(m => m.id !== moduleId));
        return { error: null };
      }

      const { error } = await supabase.from('modules').delete().eq('id', moduleId);
      if (error) throw error;

      setModules(prev => prev.filter(m => m.id !== moduleId));
      return { error: null };
    } catch (error) {
      console.error('Error deleting module:', error);
      return { error: error.message || String(error) };
    }
  };

  // ===========================================================================
  // ANNOUNCEMENT FUNCTIONS (mock-only; no table in schema)
  // ===========================================================================
  const addAnnouncement = async (announcementData) => {
    const newAnnouncement = {
      id: Date.now().toString(),
      ...announcementData,
      cohortId: announcementData.cohortId || activeCohortId,
      createdAt: new Date().toISOString()
    };
    setAnnouncements(prev => [...prev, newAnnouncement]);
    return { announcement: newAnnouncement, error: null };
  };

  const updateAnnouncement = async (announcementId, announcementData) => {
    setAnnouncements(prev =>
      prev.map(a => (a.id === announcementId ? { ...a, ...announcementData } : a))
    );
    return { error: null };
  };

  const deleteAnnouncement = async (announcementId) => {
    setAnnouncements(prev => prev.filter(a => a.id !== announcementId));
    return { error: null };
  };

  // ===========================================================================
  // EVENT FUNCTIONS (wired to cohort_custom_events)
  // ===========================================================================
  const addEvent = async (eventData) => {
    try {
      if (USE_MOCK_DATA) {
        const newEvent = { id: Date.now().toString(), ...eventData, cohortId: activeCohortId };
        setEvents(prev => [...prev, newEvent]);
        return { event: newEvent, error: null };
      }

      const payload = {
        cohort_id: eventData.cohortId || activeCohortId,
        type: eventData.type || 'other',
        title: eventData.title,
        event_date: eventData.event_date || eventData.eventDate || eventData.date,
        start_time: eventData.start_time || eventData.startTime || null,
        end_time: eventData.end_time || eventData.endTime || null,
        description: eventData.description || null
      };

      const { data, error } = await db.createCohortEvent(payload);
      if (error) throw error;

      const normalized = { ...data, source: 'cohort_custom', cohortId: data.cohort_id };
      setEvents(prev => [...prev, normalized]);
      return { event: normalized, error: null };
    } catch (error) {
      console.error('Error adding event:', error);
      return { event: null, error: error.message || String(error) };
    }
  };

  const updateEvent = async () => {
    return { error: 'Event update is not wired yet (no db method implemented).' };
  };

  const deleteEvent = async (eventId) => {
    try {
      if (USE_MOCK_DATA) {
        setEvents(prev => prev.filter(e => e.id !== eventId));
        return { error: null };
      }

      const { error } = await db.deleteCohortEvent(eventId);
      if (error) throw error;

      setEvents(prev => prev.filter(e => e.id !== eventId));
      return { error: null };
    } catch (error) {
      console.error('Error deleting event:', error);
      return { error: error.message || String(error) };
    }
  };

  // ===========================================================================
  // EXTERNAL RESOURCES FUNCTIONS (client-managed - for backwards compatibility)
  // ===========================================================================
  const updateExternalResources = async (resources) => {
    setExternalResources(resources);
    return { error: null };
  };

  // ===========================================================================
  // CONTEXT VALUE - ALL FUNCTIONS MUST BE LISTED HERE!
  // ===========================================================================
  const value = {
    // State
    user,
    isLoading,
    dataInitialized,
    isAuthenticated,
    isPasswordRecovery,
    setIsPasswordRecovery,
    currentUser,
    currentView,
    setCurrentView,
    isDarkMode,
    setIsDarkMode,
    toggleDarkMode,
    studentProgress,

    // Collections
    cohorts,
    students,
    modules,
    announcements,
    events,
    externalResources,

    // Cohort selection
    selectedCohortId,
    setSelectedCohortId,
    currentCohortId,
    currentCohort,
    currentCohortStudents,
    currentCohortModules,
    currentCohortAnnouncements,
    currentCohortEvents,
    setCurrentCohort,

    // Week selection
    selectedWeek,
    setSelectedWeek,

    // Getter functions
    getCohortById,
    getModulesForCohort,
    getVisibleModulesForCohort,
    getUsersForCohort,
    getEventsForCohort,

    // Module visibility
    toggleWeekVisibility,
    scheduleWeekVisibility,

    // Calendar events
    addMasterCalendarEvent,
    addCohortCustomEvent,
    deleteCalendarEvent,
    deleteMasterCalendarEvent,

    // Resource management
    addExternalResource,
    updateExternalResource,
    deleteExternalResource,

    // Auth
    login,
    signup,
    getUpcomingCohorts,
    logout,

    // Syllabus PDF management
    addSyllabusPdf,
    deleteSyllabusPdf,

    // Module item management
    addModuleItem,
    updateModuleItem,
    deleteModuleItem,

    // Cohort-specific module item management
    addCohortModuleItem,
    deleteCohortModuleItem,

    // Progress tracking
    markItemComplete,
    trackItemView,
    isItemComplete,
    isItemViewed,
    isModuleComplete,
    getModuleProgress,

    // Student submissions
    studentSubmissions,
    getSubmissionForItem,
    getSubmissionsWithFeedback,
    refreshStudentSubmissions,

    // Unread messages
    unreadMessageCount,
    refreshUnreadCount,

    // Settings
    defaultZoomUrl,

 // Cohort module visibility overrides (per-cohort lock/unlock)
    loadCohortVisibility,
    cohortVisibility,

 // Cohort item overrides (for cohort-specific scheduling)
    loadCohortItemOverrides,
    updateCohortItemDay,
    updateCohortItemDueDate,
    updateCohortItemOrder,
    resetCohortItemToMaster,
    cohortItemOverrides,

    // CRUD operations
    addCohort,
    updateCohort,
    deleteCohort,
    duplicateCohort,

    addStudent,
    updateStudent,
    deleteStudent,
    refreshStudents,

    addModule,
    updateModule,
    deleteModule,

    addAnnouncement,
    updateAnnouncement,
    deleteAnnouncement,

    addEvent,
    updateEvent,
    deleteEvent,

    updateExternalResources,

    // Profile updates
    updateCurrentUser: (data) => setCurrentUser(prev => ({ ...prev, ...data })),
    updateOwnProfile
  };

  return (
    <LMSContext.Provider value={value}>
      {children}
    </LMSContext.Provider>
  );
};

export const useLMS = () => {
  const context = useContext(LMSContext);
  if (!context) throw new Error('useLMS must be used within a LMSProvider');
  return context;
};
